import React from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { CelebrantEntry } from '@om/shared-types';
import * as celebrantConfig from '@om/shared-types/celebrant-config';
const { getCelebrantRoleOptions, getPredictedRoleAt } = celebrantConfig;

interface CelebrantsEditorProps {
  celebrationType: string;
  celebrants: CelebrantEntry[];
  onChange: (next: CelebrantEntry[]) => void;
  isSurprise?: boolean;
  hostEmail?: string;
  idPrefix?: string;
}

/**
 * Shared name/role/email editor for celebrants, used by Quick Create, the
 * multi-event form, and (indirectly, via the same role config) the AI chat flow.
 * Celebrant 1 is always kept; additional celebrants can be added for any
 * celebration type (e.g. twins) and removed freely.
 */
export const CelebrantsEditor: React.FC<CelebrantsEditorProps> = ({
  celebrationType,
  celebrants,
  onChange,
  isSurprise,
  hostEmail,
  idPrefix = 'quick-celebrant',
}) => {
  const roleOptions = getCelebrantRoleOptions(celebrationType);
  const roleListId = `${idPrefix}-role-options`;

  const updateCelebrant = (index: number, patch: Partial<CelebrantEntry>) => {
    const next = celebrants.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(next);
  };

  const removeCelebrant = (index: number) => {
    onChange(celebrants.filter((_, i) => i !== index));
  };

  const addCelebrant = () => {
    onChange([
      ...celebrants,
      { name: '', role: getPredictedRoleAt(celebrationType, celebrants.length), email: '' },
    ]);
  };

  return (
    <div className="space-y-3">
      <datalist id={roleListId}>
        {roleOptions.map((role) => (
          <option key={role} value={role} />
        ))}
      </datalist>

      {celebrants.map((celebrant, index) => {
        const isPrimary = index === 0;
        const rowIdPrefix = `${idPrefix}-${index}`;
        const emailValue = isPrimary && isSurprise ? (hostEmail ?? '').trim() : celebrant.email;

        return (
          <div key={rowIdPrefix} className="space-y-1.5 rounded-md border border-border/60 p-2.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${rowIdPrefix}-name`}>
                {isPrimary ? 'Celebrant name*' : `Celebrant ${index + 1} name`}
              </Label>
              {!isPrimary && (
                <button
                  type="button"
                  onClick={() => removeCelebrant(index)}
                  aria-label={`Remove celebrant ${index + 1}`}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Input
              id={`${rowIdPrefix}-name`}
              value={celebrant.name}
              onChange={(event) => updateCelebrant(index, { name: event.target.value })}
              placeholder={isPrimary ? 'Riya' : 'Arjun'}
              required={isPrimary}
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`${rowIdPrefix}-role`}>Celebrant role</Label>
                <Input
                  id={`${rowIdPrefix}-role`}
                  list={roleListId}
                  value={celebrant.role}
                  onChange={(event) => updateCelebrant(index, { role: event.target.value })}
                  placeholder={getPredictedRoleAt(celebrationType, index)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`${rowIdPrefix}-email`}>Celebrant email (optional)</Label>
                <Input
                  id={`${rowIdPrefix}-email`}
                  type="email"
                  value={emailValue}
                  onChange={(event) => !(isPrimary && isSurprise) && updateCelebrant(index, { email: event.target.value })}
                  placeholder={isPrimary && isSurprise ? 'Uses host email' : 'riya@example.com'}
                  readOnly={isPrimary && isSurprise}
                />
              </div>
            </div>
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={addCelebrant} className="gap-1.5">
        <Plus className="h-3.5 w-3.5" />
        Add another celebrant
      </Button>
    </div>
  );
};
